import { useEffect, useState, type MouseEvent } from 'react';
import { ArrowLeft, ArrowRight, ArrowUpRight, ChevronLeft, ChevronRight, Maximize2, X } from 'lucide-react';
import { coreProjects, gptImages, humanCcdImages, misteaImages, officialTools, robotVacuumImages } from './data';
import { projectDemos } from './projectDemos';
import { projectViewerAssets } from './projectViewerAssets';
import ProjectDemoView from './components/ProjectDemoView';

type DetailProject = {
  slug: string;
  index: string;
  eyebrow: string;
  title: string;
  summary: string;
  status: string;
  facts: { label: string; value: string }[];
  sections: { id: string; label: string; title: string; body: string; points?: string[] }[];
  images: readonly string[];
  video?: { src: string; poster: string; title: string; meta: string };
};

type ToolDetail = DetailProject & { publicEvidence: string };

const details: DetailProject[] = [
  {
    slug: 'msn-ai-radar', index: '14', eyebrow: 'AI PROJECT / CONTENT RADAR',
    title: 'MSN AI\nRADAR.',
    summary: '在本地高效整理与深度挖掘视频收藏线索，通过主题分类筛选、多维价值评估与 AI 助手对话，将零散点赞转化为可直接指导创作与研究的系统化知识资产。',
    status: '单机本地运行 · 智能分类检索与 AI 对话',
    facts: [
      { label: 'PRODUCT', value: '点赞内容梳理与创作线索雷达' },
      { label: 'CAPABILITY', value: '分类筛选 · 关键词检索 · 智能对话' },
      { label: 'STACK', value: 'Python · Web UI · AI Agent' },
      { label: 'DELIVERY', value: '动态交互演示 · 可编辑独立 PPT' },
    ],
    sections: [
      { id: 'context', label: '01 / CONTEXT', title: '从“随手收藏”到“系统整理”。', body: '短视频日常收藏量巨大，但常常“存完即遗忘”。MSN 点赞雷达通过本地化解析与沉淀，对点赞视频建立结构化索引，让有价值的技术方案、工作流和行业案例能够随时被检索和调取。' },
      { id: 'filtering', label: '02 / RETRIEVAL', title: '多维度分类与实时关键词检索。', body: '支持按 AI 模型、编程开发、Agent、工作流、视频剪辑等分类标签一键筛选，同时具备毫秒级关键词模糊搜索，迅速从数百条记录中定位目标条目并查看详细分析。' },
      { id: 'ai', label: '03 / AI ASSISTANT', title: '内置智能对话，围绕线索深度追问。', body: '雷达内嵌 AI 助手，能够结合选中条目的标题、描述与技术拆解展开深入研讨，提炼核心看点，解答技术实现逻辑，让内容浏览直接自然延伸为深度思考与讨论。' },
      { id: 'workflow', label: '04 / VALUE', title: '赋能实际创作，形成从灵感到落地的闭环。', body: '帮助创作者在策划选题、探索技术方案和搭建自动化流程时快速获得参考依据。单机独立运行保障数据安全，让每一条收藏都能真正转化为实际生产力。' },
    ],
    images: [
      '/assets/core-projects/msn-ai-radar/01-overview.jpg',
      '/assets/core-projects/msn-ai-radar/02-filter.jpg',
      '/assets/core-projects/msn-ai-radar/03-breakdown.jpg',
      '/assets/core-projects/msn-ai-radar/04-search.jpg'
    ],
  },
  {
    slug: 'msn-task-workspace',
    index: '13',
    eyebrow: 'APPLICATION / AI TASK WORKSPACE',
    title: 'MSN TASK\nWORKSPACE.',
    summary: 'MSN 任务工作台：面向个人创作与项目执行的桌面应用，将项目总览、当前任务、稍后再做和专注计时放在统一界面中，并接入可执行任务操作的 AI 助手。',
    status: '桌面定制版本 · 操作演示与可编辑 PPT',
    facts: [
      { label: 'PRODUCT', value: '桌面任务与专注系统' },
      { label: 'WORKFLOW', value: '目标拆解 · 暂缓恢复 · 专注计时' },
      { label: 'STACK', value: 'Vue 3 · Pinia · Tauri 2' },
      { label: 'DELIVERY', value: '界面截图 · 3段GIF · 可编辑PPT' },
    ],
    sections: [
      { id: 'context', label: '01 / CONTEXT', title: '把当前执行与后续计划分开。', body: '任务既需要随时收集，也需要明确哪些现在做、哪些等条件成熟后再开始。工作台以当前执行为重点，同时保留项目总览、候选选题与完成记录。' },
      { id: 'design', label: '02 / DESIGN', title: '统一山景视觉与稳定的导航。', body: '采用深色底、克制的暖橙强调与山景视觉。首页呈现下一步、整体进度和任务入口，其他页面沿用同一套导航与视觉，使任务详情、专注与助手之间可以连续切换。' },
      { id: 'workflow', label: '03 / WORKFLOW', title: '暂缓任务真正移出当前执行。', body: '子任务勾选会更新进度。暂缓功能移动原任务并保留其内容，需要时恢复到原清单；专注模式可关联具体任务，并支持计时与暂停。' },
      { id: 'mobile', label: '04 / MOBILE SYNC', title: '多端访问与移动端工作流延伸。', body: '支持通过移动端进行日常任务查看、状态标记与快速收集，数据通过本地网关实时同步，配合乐观冲突保护与周期性检查，让桌面深度规划与手机碎片化推进无缝衔接。' },
      { id: 'ai', label: '05 / AI INTEGRATION', title: '让助手读取任务，并记录实际操作。', body: '任务助手集成清单读取、添加、更新、完成、暂缓与恢复工具，写操作以保存结果为准，辅助推进复杂目标拆解。' },
    ],
    images: ['/assets/core-projects/msn-task-workspace/home.jpg', '/assets/core-projects/msn-task-workspace/task.gif', '/assets/core-projects/msn-task-workspace/defer.gif', '/assets/core-projects/msn-task-workspace/focus.gif', '/assets/core-projects/msn-task-workspace/assistant.jpg'],
  },
  {
    slug: 'msn-content-publishing',
    index: '12',
    eyebrow: 'AI CONTENT / WORKFLOW & PUBLISHING',
    title: 'FROM CREATIVE IDEA.\nTO PUBLISHED CONTENT.',
    summary: 'MSN AI 内容生产与发布工作台：把数字人口播、知识动画、封面与发布管理接在一起，让视觉表达与内容运营形成可复用的工作流程。',
    status: '代表成片已完成 · 已有双账号真实发布案例',
    facts: [
      { label: 'DELIVERABLE', value: '成片 · 横竖封面 · 发布工作台' },
      { label: 'CONTENT', value: '数字人口播 × 音乐驱动知识动画' },
      { label: 'WORKFLOW', value: '双账号分流 · 资产包核对 · 状态追踪' },
      { label: 'RESULT', value: '双账号发布记录与平台回执' },
    ],
    sections: [
      { id: 'context', label: '01 / CHALLENGE', title: '让做好的内容，有清楚的发布去向。', body: '制作过程里的文案、成片、横竖封面与账号任务分散在不同环节。本项目将这些内容集中核对，使人工审片、账号分配、发布任务与结果回收衔接起来。' },
      { id: 'visual', label: '02 / CREATIVE', title: '用两种内容表达，展示视觉与节奏判断。', body: '数字人口播通过人物、镜头变化与科技风图文组织观点；知识动画围绕音乐乐句安排信息揭晓、画面运动与光影。以实际成片说明创作能力，而不是用工具数量代替作品。' },
      { id: 'pipeline', label: '03 / WORKFLOW', title: '全链路资产集中组织与交付。', body: '统一管理文案、成片、双版封面与账号分发，通过结构化发布清单核对标题、话题与正文，将制作阶段直接引向规范交付。' },
      { id: 'result', label: '04 / DELIVERY', title: '用真实发布案例，验证制作到运营的衔接。', body: '已完成《最后一个视频，我说了四遍》与《收藏了那么多教程，你真正用过几条？》双账号发布案例，保留代表成片、独立横竖封面和平台发布记录。' },
      { id: 'standards', label: '05 / QUALITY GATE', title: '把人工审美与合规核验保留在关键节点。', body: '在自动化流程中设置必要的人工审阅卡点，确保成片视听质量、文案准确性与平台规范，实现高效与品质的平衡。' },
    ],
    images: ['/assets/core-projects/msn-content-publishing-case.png', '/assets/core-projects/msn-content-publishing-workbench.png'],
    video: { src: '/assets/core-projects/msn-content-publishing-film.mp4', poster: '/assets/core-projects/msn-content-publishing-film.jpg', title: '收藏不等于学会', meta: '知识动画代表作 · 25秒 · 实际成片' },
  },
  {
    slug: 'digital-human-ai-review',
    index: '11',
    eyebrow: 'CONTENT SYSTEM / PRODUCTION PIPELINE',
    title: '自动化剪辑与\n内容生产全流程',
    summary: '把脚本与素材产出、配音与数字人口播、智能剪辑、字幕图文包装、横竖封面和发布包串成可复用的内容生产流程，再通过发布工作台核对状态与回执。《我开始用 AI，鉴定 AI 博主了》是这套流程的代表成品。',
    status: '可复用制作流程 · 已有完整成片与发布案例',
    facts: [
      { label: 'INPUT', value: '选题脚本 · 最终配音 · 镜头素材' },
      { label: 'PRODUCTION', value: '数字人口播 · 智能剪辑 · 图文包装' },
      { label: 'DELIVERY', value: '完整成片 · 横竖封面 · 发布包' },
      { label: 'OPERATIONS', value: '账号分流 · 发布状态 · 平台回执' },
    ],
    sections: [
      {
        id: 'context', label: '01 / SCRIPT & ASSETS', title: '先组织脚本，再产出与内容对应的素材。',
        body: '将选题整理为可以讲清楚的脚本，规划分镜、关键词与画面节点；结合生成图像、参考素材和实际界面，为后续剪辑准备可以直接使用的画面。素材与文案按作品归档，保持版本对应。',
      },
      {
        id: 'direction', label: '02 / VOICE & DIGITAL HUMAN', title: '最终配音驱动数字人口型与画面节奏。',
        body: '先确定当期最终音频，再生成数字人口播，避免声音与口型版本错配。根据分镜安排人物小窗、圆形头像或主体画面，保留人物原片与当前成片，便于继续调整。',
        points: ['脚本、配音与口型使用同一版本', '人物镜头大小与位置服务讲解', '生成素材与实际画面按内容组织', '以人工视听检查确认最终表达'],
      },
      {
        id: 'editing', label: '03 / EDITING & PACKAGING', title: '按文稿剪辑，让字幕、图文与音效跟着讲解走。',
        body: '结合 V21 口播工作流定位词句、审核停顿与剪辑节点，再安排字幕、关键词高亮、配图和音效。科技图文、双栏对比和人物小窗随内容切换，将重复制作步骤组织为可复用的包装流程。',
      },
      {
        id: 'delivery', label: '04 / COVERS & PUBLISHING', title: '从完整成片到封面、发布包与平台回执。',
        body: '同一条作品整理横竖封面、标题、正文与话题，形成对应的发布包。通过内容发布工作台核对成片、账号分配、任务状态和平台回执；发布前保留人工审片，发布后继续复盘。',
      },
      {
        id: 'result', label: '05 / REPRESENTATIVE FILM', title: '打开代表成品，直接观看完整视频。',
        body: '《我开始用 AI，鉴定 AI 博主了》展示数字人口播、写实生成素材、完整字幕、配乐与科技图文的综合效果。本页保留完整成片，作为生产流程的成果示例。',
      },
    ],
    images: [],
    video: {
      src: '/assets/portfolio-media/ai/ai-digital-human-review.mp4',
      poster: '/assets/portfolio-covers/ai/ai-digital-human-review.webp',
      title: '我开始用 AI，鉴定 AI 博主了',
      meta: '全流程代表成品 · 部分画面为 AI 生成示意 · 1920 × 1080',
    },
  },
  {
    slug: 'ai-video-automation',
    index: '01',
    eyebrow: 'CORE PROJECT / VERIFIED RERUN',
    title: 'AI VIDEO\nAUTOMATION.',
    summary: '把已确认的脚本、本轮旁白、字幕和九张镜头素材接入统一入口，完成校验、时间线分配、合成与质量检查。',
    status: '2026-07-22 技术合成链复跑通过',
    facts: [
      { label: 'OUTPUT', value: '1080 × 1920 / H.264 + AAC' },
      { label: 'TIMELINE', value: '39.37 秒 / 9 个镜头' },
      { label: 'GATE', value: '3 / 3 单元测试通过' },
      { label: 'REPEATABILITY', value: '与历史成片 SHA-256 一致' },
    ],
    sections: [
      { id: 'context', label: '01 / CONTEXT', title: '不是一次成片，而是一条可检查的生产路径。', body: '问题不在于能否导出 MP4，而在于同一批已确认输入能否按明确规则再次合成，并在失败时给出可定位的原因。' },
      { id: 'contract', label: '02 / INPUT CONTRACT', title: '先定义输入，再允许渲染。', body: '统一入口只接收已经确认的脚本、旁白、字幕和镜头素材；选题、脚本审批、生图与最终声音选择不在本次自动入口内。', points: ['检查 9 张镜头素材是否齐全并按序命名', '确认旁白与字幕文件存在且可读取', '输入缺失或格式错误时在渲染前停止', '使用独立输出目录，不覆盖源项目和历史成片'] },
      { id: 'timeline', label: '03 / TIMELINE', title: '让真实音频决定时间线。', body: '系统读取旁白长度，再按规则把镜头分配到总时长中；字幕时间映射和镜头切换都以实际输入为基准，而不是用宣传式的固定效率数字。' },
      { id: 'qa', label: '04 / COMPOSITE & QA', title: '合成完成，还要留下机器与人工都能看的证据。', body: '本次输出为 1080×1920、30fps、H.264/AAC。3/3 单元测试通过，音视频时长差 0.013 秒，成片哈希与历史输出一致；同时保留审片图、渲染记录和发布说明。' },
      { id: 'value', label: '05 / DELIVERY VALUE', title: '把重复步骤交给系统，把审美判断留给人。', body: '可复用价值来自输入门禁、时间线、合成规格和质量记录可以重复执行。构图、版权、最终配音、BGM、封面和发布决策仍由外部流程或人工确认。' },
      { id: 'limits', label: '06 / LIMITS', title: '当前复跑验证的是技术合成链。', body: '当前复跑验证的是技术合成链，不是选题、生图或配音的全自动产生。横屏分镜转竖屏仍有中心裁切风险；公开成片、正式声音与发布结果均未在本阶段新增。' },
    ],
    images: coreProjects[0].images,
  },
  {
    slug: 'ai-3d-kinetic-workflow',
    index: '02',
    eyebrow: 'CORE PROJECT / 3D PIPELINE',
    title: 'HUNYUAN 3D &\nKINETIC PIPELINE.',
    summary: '结合混元 3D 生成、分件拓扑优化、PBR 材质调优与 Three.js 独立动效枢轴计算，实现三维机械资产的 Web 端高保真交互落地。',
    status: '已验证并接入本站 3D 首屏系统',
    facts: [
      { label: 'MODELS', value: '3 组高精机械 GLB 资产' },
      { label: 'PIPELINE', value: 'Hunyuan 3D ➔ 分件 ➔ WebGL 交互' },
      { label: 'SHADING', value: 'PBR 金属度 + 晶体透光 + Bloom' },
      { label: 'INTERACTION', value: 'Raycaster 拾取 + 动态悬停反馈' },
    ],
    sections: [
      { id: 'context', label: '01 / 资产生成', title: '从概念生图到三维多视角几何重建。', body: '使用混元 3D 多模态生成管线，由提示词或多角度设定图生成三维网格，捕捉机械结构的复杂曲面与机械构件。' },
      { id: 'topology', label: '02 / 拓扑与分件', title: '结构拆分、法线烘焙与动效枢轴标定。', body: '在 3D 视口中剔除杂面与重叠顶点，分离旋转部件与固定外环支架，标定各自旋转中心（Pivot），避免运行时偏心失真。' },
      { id: 'material', label: '03 / 材质与环境', title: 'PBR 物理着色与高折射光学晶体。', body: '配置金属度、深石墨哑光粗糙度、古铜色重映射与 MeshPhysicalMaterial 高透射玻璃晶体，结合 RoomEnvironment IBL 提供通透自然的环境反射。' },
      { id: 'interaction', label: '04 / WebGL 交互', title: 'Three.js 实时渲染管线与光标响应。', body: '接入统一渲染循环与 Selective Bloom 发光通道，配合原生鼠标 Raycaster 命中检测与悬停金色高光反馈，达成电影级动态呈现。' },
    ],
    images: coreProjects[1].images,
  },
  {
    slug: 'ai-learning-skill-system',
    index: '03',
    eyebrow: 'CORE PROJECT / SYSTEM AUDIT',
    title: 'LEARNING TO\nREUSABLE SKILLS.',
    summary: '把收藏、教程和项目复盘组织成可检索、可路由、可验证的能力库，并把“收集过”与“能稳定调用”明确分开。',
    status: '统计口径已于 2026-07-22 复算',
    facts: [
      { label: 'SOURCE TRACE', value: '来源记录' },
      { label: 'STATUS GATE', value: '状态门禁' },
      { label: 'REGISTRY CHECK', value: '注册与安装校验' },
      { label: 'EVIDENCE LOOP', value: '项目调用与证据回写' },
    ],
    sections: [
      { id: 'context', label: '01 / INTAKE', title: '内容进入学习库时，先保留来源与状态。', body: '收藏、教程、项目复盘和真实任务记录进入候选区；每条输入先回答来源、用途、许可与是否值得继续处理，而不是先被包装成项目。' },
      { id: 'distill', label: '02 / SCAN & DISTILL', title: '扫描信息，再提炼可复用的方法。', body: '把长内容拆成问题、输入、步骤、判断标准、失败边界和可复用场景。无法验证或仅有观点的内容继续停留在学习记录，不直接升级为 Skill。' },
      { id: 'register', label: '03 / REGISTER & TEST', title: '注册只是开始，测试才决定状态。', body: '候选 Skill 需要明确入口、依赖、验证方式和证据位置，再经过真实测试或等价证据复验；失败、阻塞和第三方内容均保留原状态。', points: ['434 条是学习目录记录，不是项目数量', '49 个是注册表登记项，不是自主开发产品数量', '24 个 ACTIVE 只表示当前启用状态', '22 个只是公开候选审查项，尚未批准公开'] },
      { id: 'execute', label: '04 / CODEX EXECUTION', title: '在真实任务中调用，而不是停在说明文件。', body: 'Codex 根据任务、输入和边界路由到合适能力；执行后必须留下实际文件、测试结果或可核查报告。网页能打开、脚本存在或目录已登记，都不单独等于能力完成。' },
      { id: 'writeback', label: '05 / EVIDENCE WRITEBACK', title: '输出证据、状态回写与复盘形成闭环。', body: '成功、失败和阻塞状态写回记录；可复现的命令、产物和限制进入报告，下一次任务再用新证据更新，而不是沿用旧口径。统计由脚本复算，避免手填数字漂移。' },
      { id: 'limits', label: '06 / PUBLIC BOUNDARY', title: '公开系统结构，不公开私人学习内容。', body: '公开页优先呈现任务闭环、来源记录、测试门禁、调用证据和复盘。个人收藏、私人路径、受许可限制的来源与未通过公开审查的能力继续留在内部。' },
    ],
    images: coreProjects[2].images,
  },
  {
    slug: 'xiaohei-ai-video',
    index: '04',
    eyebrow: 'CORE PROJECT / ORIGINAL FILM',
    title: 'PERSONALITY\nMEETS AI.',
    summary: '《性格与AI》把 INFJ、高敏感思维和 AI 协作的抽象关系，转换成 6 个连续手绘隐喻场景与 22 个可读状态画面。',
    status: '原创成片已完成并接入',
    facts: [
      { label: 'OUTPUT', value: '1920 × 1080 / H.264 + AAC' },
      { label: 'RUNTIME', value: '40.555 秒 / 12fps' },
      { label: 'STRUCTURE', value: '6 个场景 / 22 个状态画面' },
      { label: 'CAPTIONS', value: '22 条字幕 / 音画完整解码通过' },
    ],
    sections: [
      { id: 'context', label: '01 / IDEA', title: '性格不是标签，而是一种处理信息的方式。', body: '短片从 INFJ 与高敏感特质切入，讨论观察细节、深度思考、拆解表达和规划路径，如何在 AI 工作流中被进一步放大。' },
      { id: 'pipeline', label: '02 / VISUAL TRANSLATION', title: '把抽象观点转成可以被看见的物理隐喻。', body: '6 个场景依次使用 AI 放大镜、信号织机、想法剖面机、复杂地图、外接大脑与优势种子，把口播中的认知过程转换成连续动作。', points: ['放大镜：AI 放大既有思维特质', '信号织机：将细微信息整理成提示词与工作流', '剖面机：把脑内想法拆解为清楚表达', '地图与系统盒：观察、思考、规划和系统化', '外接大脑与能力之树：从简单问答走向能力放大'] },
      { id: 'production', label: '03 / PRODUCTION', title: '声音确定时间，状态画面承担节奏。', body: '正式人声、BGM 与 22 条字幕形成 40.555 秒时间线；画面采用 6 个主场景、22 个完整状态帧的阶梯替换机制，让每个隐喻随口播逐步成立。' },
      { id: 'evidence', label: '04 / QA', title: '成片、时间线和技术检查共同构成交付证据。', body: '网页接入文件为原 SOP 渲染输出的无损副本。视频为 1920×1080、H.264、12fps，音频为 AAC 48kHz 双声道；完整 FFmpeg 解码、字幕时间和资源一致性检查均已通过。' },
      { id: 'limits', label: '05 / PUBLIC BOUNDARY', title: '只展示原创成片，不公开内部参考素材。', body: '页面公开《性格与AI》成片与可核查制作事实；内部参考视频、私人路径、生成过程中的失败版本和敏感工作文件不进入网站。' },
    ],
    images: coreProjects[3].images,
    video: {
      src: '/media/personality-and-ai.mp4',
      poster: '/media/personality-and-ai-poster.jpg',
      title: '《性格与AI》原创横屏动态图解',
      meta: '40.555 秒 · 1920×1080 · H.264 + AAC',
    },
  },
  {
    slug: 'msn-video-watermark-removal',
    index: '05',
    eyebrow: 'CORE PROJECT / LOCAL AI TOOL',
    title: 'REMOVE THE MARK,\nKEEP THE FRAME.',
    summary: 'MSN 视频去水印系统把手动画框作为搜索范围，再从逐帧画面中提取真实文字像素，只修复水印实际出现的位置与时刻。',
    status: '8.04 秒猫咪成片已完成并接入',
    facts: [
      { label: 'OUTPUT', value: '1280 × 720 / H.264 + AAC' },
      { label: 'RUNTIME', value: '8.042 秒 / 原音保留' },
      { label: 'MASK', value: '动态判空 + 文字像素提取' },
      { label: 'ENGINE', value: 'VSR · LaMa / 本地处理' },
    ],
    sections: [
      { id: 'context', label: '01 / PROBLEM', title: '矩形框只负责告诉系统去哪里找。', body: '早期方案把整个手绘矩形持续送入修复模型，夜空、云层和角落背景也被一起重绘，因此容易出现方块、白斑和边缘接缝。' },
      { id: 'selection', label: '02 / ROI', title: '保留可移动、可缩放的多框交互。', body: '用户在视频上框出左上角、右下角等大致区域，选区支持新增、移动、缩放、删除与清空；坐标按视频原始分辨率归一化后传给后端。' },
      { id: 'mask', label: '03 / DYNAMIC MASK', title: '只提取框内真实存在的文字像素。', body: '后端逐帧分析 ROI 的亮度、边缘和局部变化，生成紧贴文字轮廓的二值 Mask。检测不到水印的帧直接保留原图，避免静态 Mask 误伤闪烁间隔中的背景。', points: ['选区只是搜索边界，不是最终修复面积', '文字 Mask仅做 1–2px 微量膨胀', '边缘使用约 3px 高斯羽化过渡', '无有效像素时跳过 LaMa 修复'] },
      { id: 'repair', label: '04 / LAMA REPAIR', title: '把模型算力集中在真正需要修复的像素上。', body: 'VSR · LaMa 只接收当前帧的精细 Mask，修复后再与未遮罩区域合成；视频轨编码完成后重新封装原始 AAC 音轨。' },
      { id: 'evidence', label: '05 / VERIFIED OUTPUT', title: '真实猫咪视频完成端到端处理。', body: '作品集接入的成片为 1280×720、约 8.042 秒、H.264 视频与 AAC 双声道音频。文件已完成完整解码检查，可直接在详情页播放。' },
      { id: 'limits', label: '06 / LIMITS', title: '复杂动态水印仍需要更紧的选区与阈值复核。', body: '高透明度、彩色渐变、快速移动或与背景亮度接近的水印，可能需要调整阈值或增加跟踪；公开页面只展示当前已验证流程和成片，不宣称适用于所有视频。' },
    ],
    images: coreProjects[4].images,
    video: {
      src: '/media/msn-watermark-removal.mp4',
      poster: coreProjects[4].images[0],
      title: 'MSN · 蓝色猫咪视频动态精细去水印成片',
      meta: '8.042 秒 · 1280×720 · H.264 + AAC',
    },
  },
  {
    slug: 'v21-video-editing',
    index: '06',
    eyebrow: 'CORE PROJECT / VIDEO EDITING',
    title: 'V21 ORAL BROADCAST\nEDITING & PACKAGING.',
    summary: '围绕口播视频生产，整合粗剪审核、字幕、文字包装与封面编辑，并将工作流整理为可迁移的便携环境。',
    status: '本机与便携目录已交付，持续迭代',
    facts: [
      { label: 'ROLE', value: '业务需求定义 · AI 辅助开发 · 调试集成' },
      { label: 'PIPELINE', value: '粗剪审核 + 文字包装 + 封面同步' },
      { label: 'BENCHMARK', value: '审核快速编码 + 成品独立生成' },
      { label: 'DELIVERY', value: '便携化环境 · 使用说明与复盘' },
    ],
    sections: [
      {
        id: 'context',
        label: '01 / CONTEXT',
        title: '结合拍摄剪辑业务，定义真实的生产闭环。',
        body: '文案标题与实际口播正文分开管理，修改标题可同步联动封面与包装文字，解决人工重复修改导致的错漏。',
      },
      {
        id: 'features',
        label: '02 / EDIT & REVIEW',
        title: '审核剪辑范围、文字编辑与字体上传。',
        body: '支持粗剪审核范围设定、文字编辑与自定义字体上传；针对早期测试中出现的裁切残帧、手动标题被覆盖及界面加载问题完成多轮修复，确保生产界面状态稳定。',
        points: [
          '文案标题与口播正文独立解耦',
          '标题联动封面排版与包装动态展示',
          '修复裁切残帧与手动标题被覆盖',
          '界面状态与字体加载异常排障已验证',
        ],
      },
      {
        id: 'benchmark',
        label: '03 / FAST PREVIEW',
        title: '双轨编码：审核预览与成品生成解耦。',
        body: '建立快速审核预览编码基准，在审核阶段采用快速低算力编码实现毫秒级预览与拖拽；最终成片独立导出高保真母版，兼顾审核效率与交付画质。',
      },
      {
        id: 'portable',
        label: '04 / PORTABLE DELIVERY',
        title: '便携化环境整合与知识沉淀。',
        body: '将既有视频工作流整理为可启动、可迁移、带说明与验收材料的本地便携环境；内置配套模板、字体与详细复盘使用说明，支持同事快速接手。',
      },
      {
        id: 'limits',
        label: '05 / BOUNDARIES',
        title: '客观交付边界与人工门禁。',
        body: '工作流以人工判断为核心，不是无需审核的全自动智能剪辑；未核实同事长期使用反馈，部分外部字体待复测；最近更新已在便携目录内交付，但未重新打包全量历史 ZIP。企业口播成片与人物画面受版权保护，仅展示演示架构与加载状态。',
      },
    ],
    images: ['/assets/core-projects/v21-video-editing-cover.png', '/assets/core-projects/v21-video-editing-loading.png'],
  },
  {
    slug: 'local-ai-mobile',
    index: '07',
    eyebrow: 'CORE PROJECT / MOBILE AI SERVICE',
    title: 'LOCAL AI SERVICE\nON MOBILE.',
    summary: '通过 Android 客户端提交图片、图集链接与编辑要求，由电脑端调用本地 ComfyUI 工作流，处理后将结果返回手机。',
    status: 'APK 与图集编辑链路已有实测交付',
    facts: [
      { label: 'CLIENT', value: 'Android 客户端 APK' },
      { label: 'ENGINE', value: '电脑端本地 ComfyUI 工作流' },
      { label: 'LINK', value: '抖音图集解析 ➔ 逐张批处理' },
      { label: 'EVIDENCE', value: '移动端提交 ➔ 远程回传结果' },
    ],
    sections: [
      {
        id: 'context',
        label: '01 / DEMAND',
        title: '让手机随时随地调用高算力本地工作流。',
        body: '梳理移动端随时触发 AI 图像处理的操作需求，组织 Android 客户端与电脑端服务接入，打通手机与本地 GPU 算力通道。',
      },
      {
        id: 'pipeline',
        label: '02 / PIPELINE',
        title: '图集解析与批量工作流串联。',
        body: '手机端可直接提交参考图和提示词，也可输入抖音图集链接；后端自动解析图集并逐张送入本地 ComfyUI 模型工作流进行风格化与图生图编辑，完成后通过远程接口将图片回传手机。',
        points: [
          '手机端轻量提交参考图与定制提示词',
          '图集链接自动解析与逐张批量处理',
          '异步队列管理与移动端状态回显',
          '保留原工作流与版本交付记录',
        ],
      },
      {
        id: 'evidence',
        label: '03 / VERIFIED OUTPUT',
        title: '真实 APK 安装包与图集编辑链路实测交付。',
        body: '已交付 Android 客户端安装包与配套接口交付说明，完成图集解析、工作流触发及移动端结果回显的真实链路测试。',
      },
      {
        id: 'limits',
        label: '04 / BOUNDARIES',
        title: '客观运行限制与隐私边界。',
        body: '锁屏通知及手机长期后台存活性待真机进一步确认；该架构依赖电脑端保持运行，不承诺实时秒级响应或电脑离线时依然可用；原始人物图片受肖像授权限制，公开页面采用脱敏功能结构示意图。',
      },
    ],
    images: ['/assets/core-projects/local-ai-mobile-schematic.png'],
  },
  {
    slug: 'super-msn-ledger',
    index: '08',
    eyebrow: 'APPLICATION PROJECT / ANDROID APP',
    title: 'SUPER MSN\nLEDGER APP.',
    summary: '围绕日常记账构建 Android 应用，覆盖本地账本、账单导入、统计与支付识别草稿，并接入电脑端账本同步服务。',
    status: '安装包已交付，真机完整链路待验收',
    facts: [
      { label: 'PLATFORM', value: 'Android 本地记账应用' },
      { label: 'MODULES', value: '本地账本 · 分类统计 · 账单导入' },
      { label: 'DRAFT GATE', value: '支付识别草稿 ➔ 人工确认入账' },
      { label: 'UI FIX', value: '窄屏与大字体金额显示修复' },
    ],
    sections: [
      {
        id: 'context',
        label: '01 / WORKFLOW',
        title: '提出记账需求，AI 辅助开发与迭代。',
        body: '从日常真实记账痛点出发，设计记账与核账交互，组织 AI 辅助代码实现、异常定位、数据核对与模拟器验收。',
      },
      {
        id: 'features',
        label: '02 / FEATURES',
        title: '本地账本、导入与草稿确认机制。',
        body: '提供本地账本存储、月度分类统计与多格式账单导入；引入支付识别草稿箱机制，所有识别项必须由用户核对后手动确认入账，避免自动化误判造成账目混乱。',
        points: [
          '多格式账单导入与本地数据持久化',
          '支付识别仅作为草稿，严守人工确认门禁',
          '本地持久同步队列与电脑端服务桥接',
          '窄屏及系统字体放大模式下的金额展示自适应修复',
        ],
      },
      {
        id: 'evidence',
        label: '03 / VERIFIED STATE',
        title: '已产出安装包与页面修复交付记录。',
        body: '已交付 APK 安装包、交接文档及修复记录，并在模拟器环境下验证了账本统计与导入流程。',
      },
      {
        id: 'limits',
        label: '04 / BOUNDARIES',
        title: '虚构演示数据与客观边界。',
        body: 'ColorOS 真机支付页识别、长期后台存活以及手机到电脑端全链路未最终确认；公开页面严格使用虚构演示数据，绝不复制真实个人账单或交易隐私；不宣称稳定商业化上线或真实商业用户增长。',
      },
    ],
    images: ['/assets/core-projects/super-msn-ledger-mockup.png'],
  },
  {
    slug: 'msn-study',
    index: '09',
    eyebrow: 'APPLICATION PROJECT / LEARNING SYSTEM',
    title: 'MSN 本科成考\n学习工作台',
    summary: '围绕政治、英语和高数组织题库、练习、模拟、错题、计划与学习统计，支持草稿自动保存、断点续做、原卷参考和英语原文同页对照。AI 导师支持图片问答、联网讲解及需确认的题库修订，电脑与手机可以连接同一套学习服务。',
    status: '现有题库与学习工作台 · 电脑 / 手机访问',
    facts: [
      { label: 'TARGET', value: '成人本科备考辅助学习' },
      { label: 'CLIENT', value: 'PC 网页 + 移动端自适应' },
      { label: 'PRACTICE', value: '题库筛选 · 自由练习 · 限时模拟' },
      { label: 'ASSISTANT', value: '图片问答 · 联网讲解 · 确认式维护' },
    ],
    sections: [
      {
        id: 'context', label: '01 / LEARNING WORKSPACE',
        title: '把资料、学习任务与实际作答放在同一个工作台。',
        body: '现有系统包含政治、英语与高数科目空间，连接理论学习、题库练习、模拟演练、错题、复习计划和学习统计。稳拿分与考前背诵作为其中的复习入口，与完整学习流程共同使用。',
      },
      {
        id: 'practice', label: '02 / PRACTICE & RESUME',
        title: '练习、模拟与草稿续做，保留每次学习的位置。',
        body: '按科目、年份、题型、试卷、知识点与关键词查题，进入刷题复习、自由模拟或限时模拟。选择答案、输入解答与切题时保存草稿和位置；重新进入时可以继续相同条件下的未完成题组。',
        points: [
          '练习筛选与未完成题组恢复',
          '草稿保存与实际提交分别处理',
          '客观题按核验状态批改，主观题对照参考自评',
          '错题复做、学习统计与计划进度关联',
        ],
      },
      {
        id: 'reference', label: '03 / SOURCE & ENGLISH',
        title: '原卷参考与英语原文同页对照，学习时随时核实。',
        body: '题目保留来源页与原卷参考，可打开题图和答案大图。英语阅读在电脑端左右对照、手机端上下同页呈现；已经整理的文字原文支持解析定位高亮，其余关联内容保留原图对照。',
      },
      {
        id: 'plan', label: '04 / PROGRESS & PLANNING',
        title: '从错题与实际作答，回到下一步学习安排。',
        body: '错题本保留复做入口，学习计划按科目与题目范围关联实际进度，区分刷题复习和模拟实战。理论章节、知识点状态、学习计时与计划任务共同构成日常复习路径。',
      },
      {
        id: 'assistant', label: '05 / AI TUTOR',
        title: '图片问答、联网讲解与有来源的题库维护。',
        body: 'AI 导师可以围绕当前题目或全科问题交流，并接收本轮上传的图片。联网讲解保留实际检索来源与本地缓存；题库审计和解析修订先形成带前后差异、来源依据的草案，用户确认后才写入并保留历史。',
        points: ['当前题目上下文与全科会话', '图片预览、上传与大图查看', '联网讲解标明检索来源', '题库修订先预览、确认后生效'],
      },
      {
        id: 'mobile', label: '06 / DESKTOP & MOBILE',
        title: '电脑深入复习，手机接着查看与练题。',
        body: '保留桌面启动入口，并提供受访问控制的手机访问方式，连接同一套题库与学习数据。手机布局包含快捷导航、题图放大、图片问答和练习自动保存，具体访问依赖电脑服务及网络状态。',
      },
      {
        id: 'limits',
        label: '07 / EVIDENCE',
        title: '按实际来源展示功能和学习依据。',
        body: '功能介绍依据现有源码与升级文档整理。扫描题和公式以原图为准，文字原文逐题整理范围会明确标示，联网讲解保留来源供继续核对；演示使用隔离示例数据。',
      },
    ],
    images: ['/assets/core-projects/msn-study-mockup.png'],
  },
  {
    slug: 'finance-motion',
    index: '10',
    eyebrow: 'CONTENT CASE / EDITABLE PACKAGING',
    title: 'FINANCE ORAL\nDYNAMIC PACKAGING.',
    summary: '将财经口播中的量价与经济概念转译为章节、动态图解、关键词与逐句素材安排，保留后续人工修改空间。',
    status: '可编辑时间线、样片与素材包已生成',
    facts: [
      { label: 'TYPE', value: '财经口播内容包装案例' },
      { label: 'FORMAT', value: '横屏图解适配竖屏排版' },
      { label: 'COMPONENTS', value: '动态图解 · 透明关键词 · 章节卡' },
      { label: 'EDITABILITY', value: '字幕与属性可二次微调' },
    ],
    sections: [
      {
        id: 'context',
        label: '01 / TRANSLATION',
        title: '将抽象概念视觉化，辅助视频包装。',
        body: '定义包装需求与视觉结构，组织剪辑时间线编辑、图形组件制作和关键画面检查，将财经口播中的量价知识转化为直观可读的视觉语言。',
      },
      {
        id: 'assets',
        label: '02 / ASSETS & WORKFLOW',
        title: '图解组件、透明关键词与逐句剪辑指引。',
        body: '将量价概念拆解为可编辑的图解、透明关键词及逐句素材安排；横屏图解精准适配竖屏口播排版，开头信息推进、章节编号、动态演示与结尾回顾层层递进。',
        points: [
          '横屏动态图解适配竖屏口播画面布局',
          '透明通道关键词与数据高亮浮层',
          '逐句对应、停留时间建议与剪辑实施步骤',
          '字幕、文字内容和关键组件属性支持二次修改',
        ],
      },
      {
        id: 'evidence',
        label: '03 / EVIDENCE',
        title: '已生成可编辑时间线、包装升级说明与示意素材包。',
        body: '核验包含包装升级说明、检查版抽查样片以及横屏素材包；证实了从概念拆解、素材制作到时间线包装的可行性。',
      },
      {
        id: 'limits',
        label: '04 / BOUNDARIES',
        title: '示意数据说明与权限控制。',
        body: '图解素材为解释形态的示意数据，不构成任何投资建议；当前阶段完成检查版与关键段落验证，尚无全片连续听审和高画质母版最终验收；企业原视频与人物肖像未经单独对外授权，公开页面仅展示脱敏图解示意。',
      },
    ],
    images: ['/assets/core-projects/finance-motion-diagram.png'],
  },
];

const toolImage = (slug: string) => {
  const image = officialTools.find((tool) => tool.slug === slug)?.image;
  return image ? [image] : [];
};

const toolDetails: ToolDetail[] = [
  {
    slug: 'douyin-archive-workflow', index: 'T1', eyebrow: 'SMALL SYSTEM / PRIVACY GATED', title: 'REVIEW BEFORE\nYOU ARCHIVE.',
    summary: '把公开链接、下载任务、人工审核、去重、元数据与本地内容库连接起来；作品集展示编排和归档能力，不展示私人账号或平台敏感实现。',
    status: '正式保留 / 本轮未联网复跑', publicEvidence: '仅公开抽象流程与汇总证据',
    facts: [
      { label: 'HISTORY', value: '2 个历史真实批次' }, { label: 'GATE', value: '人工审核后再导入' },
      { label: 'ARCHIVE', value: 'manifest / 摘要 / 导入结果' }, { label: 'PUBLIC LIMIT', value: '账号、Cookie、私人内容 0 条公开' },
    ],
    sections: [
      { id: 'context', label: '01 / PURPOSE', title: '下载不是价值，受控地进入内容库才是。', body: '项目面向学习与内容生产的前置素材环节：先接收公开链接，再按批次下载、去重、审核和归档，避免私人收藏直接混入正式内容库。' },
      { id: 'flow', label: '02 / FLOW', title: '链接 → 任务 → 审核 → 去重 → 归档。', body: '主页或混合任务进入离线缩略图审核；人工勾选后才复制到目标目录，同时留下 manifest、摘要、重复跳过与导入结果。', points: ['单条与批量输入采用不同审核门禁', '图文作品可整理为竖屏媒体供后续检查', '未选择内容留在内部批次，便于复核', '内容库只接收人工确认的结果'] },
      { id: 'evidence', label: '03 / VERIFIED EVIDENCE', title: '有真实历史结果，但没有伪装成本轮在线验收。', body: '专项审计核查到 2 个真实批次及视频、文本、缩略图和导入记录；此前对 67 个项目侧 Python 文件完成语法解析。本阶段没有登录、注入 Cookie 或发起新的平台下载。' },
      { id: 'limits', label: '04 / SAFETY', title: '平台与私人数据边界优先。', body: 'Cookie、登录态、Token、账号、私人链接、私人收藏、完整配置和可直接复用的平台绕过细节均不进入网站。公开视觉证据等待专门的授权样例与脱敏复跑。' },
    ], images: toolImage('douyin-archive-workflow'),
  },
  {
    slug: 'product-video-director-pack', index: 'T2', eyebrow: 'SMALL SYSTEM / RENDERED SAMPLE', title: 'PRODUCT IMAGE\nTO DIRECTOR PACK.',
    summary: '从商品图和卖点组织传播钩子、九镜方案、关键帧与声音建议，并用本地素材验证为可播放竖屏样片。',
    status: '30 秒样片已解析', publicEvidence: '真实代表帧已装配',
    facts: [
      { label: 'INPUT', value: '1 张商品图 + 卖点' }, { label: 'PLAN', value: '9 个独立场景' },
      { label: 'SAMPLE', value: '30 秒 / 1080×1920' }, { label: 'FORMAT', value: 'H.264 + AAC' },
    ],
    sections: [
      { id: 'context', label: '01 / INPUT', title: '先把产品事实变成可执行镜头。', body: '输入包含商品图、卖点、目标人群和候选时长。缺少商业授权、真人素材或关键事实时，导演包必须明确缺口。' },
      { id: 'flow', label: '02 / DIRECTOR PACK', title: '钩子、镜头、关键帧和声音分别交付。', body: '输出将传播结构、九镜场景、关键帧提示与声音建议拆开，便于后续生成、拍摄或剪辑协作；任务包不等于平台自动生成最终商业成片。' },
      { id: 'evidence', label: '03 / SAMPLE', title: '真实样片证明排版与渲染链可走通。', body: '当前样片为 30 秒、1080×1920、30fps 的 H.264/AAC 文件，九个独立画面可被识别；用途是验证导演和排版方案，不含商业模型生成的真人动作。' },
      { id: 'limits', label: '04 / BOUNDARY', title: '商业素材与最终质量仍需项目级验收。', body: '真实产品授权、品牌规范、生成模型、音乐版权与平台投放效果均不在当前证据范围内。' },
    ], images: toolImage('product-video-director-pack'),
  },
  {
    slug: 'ppt-generation-workbench', index: 'T3', eyebrow: 'SMALL SYSTEM / EDITABLE DELIVERY', title: 'STRUCTURE FIRST,\nTHEN SLIDES.',
    summary: '从主题、受众、页数与风格生成故事线、总览和页面任务，并用真实 8 页可编辑 PPTX 与逐页预览验证交付。',
    status: '工作台与 8 页 PPTX 已核查', publicEvidence: '真实总览图已装配',
    facts: [
      { label: 'INPUT', value: '主题 / 受众 / 页数 / 风格' }, { label: 'DECK', value: '8 页可编辑 PPTX' },
      { label: 'PREVIEW', value: '8 张逐页 PNG + 总览' }, { label: 'GATE', value: '每套主题重新视觉 QA' },
    ],
    sections: [
      { id: 'context', label: '01 / BRIEF', title: '输入决定故事线，不用模板替代事实。', body: '工作台先记录主题、听众、页数、资料与视觉方向，再组织大纲和页面任务。事实数据必须有来源。' },
      { id: 'flow', label: '02 / WORKBENCH', title: '大纲 → 总览 → 页面拆解 → 可编辑交付。', body: '浏览器工作台负责结构规划、页面组织和任务包；现有示例随后产出真实 PPTX、逐页 PNG 与总览图。规划工具和文件生成结果在页面中分别标注。' },
      { id: 'evidence', label: '03 / VERIFIED OUTPUT', title: '现有样例不是只有页面原型。', body: '已核查 8 页可编辑 PPTX、8 张逐页预览和总览图；浏览器输入、风格切换与任务生成交互也有历史验收记录。' },
      { id: 'limits', label: '04 / LIMITS', title: '一键不等于免审。', body: '每个新主题仍需核对资料来源、文字溢出、图像授权、版式一致性和最终演示效果。' },
    ], images: toolImage('ppt-generation-workbench'),
  },
  {
    slug: 'ai-product-detail-page', index: 'T4', eyebrow: 'SMALL SYSTEM / COMMERCE PLANNING', title: 'PRODUCT FACTS\nINTO PAGE SYSTEMS.',
    summary: '把商品事实、卖点和受众组织成详情页模块、六图规划与可编辑提示词，保留最终批量生图和合规复核的人工作业。',
    status: '本地交互与导出已验证', publicEvidence: '真实演示主图已装配',
    facts: [
      { label: 'INPUT', value: '商品 / 卖点 / 人群 / 平台' }, { label: 'PLAN', value: '6 图规划' },
      { label: 'OUTPUT', value: '结构 / 文案 / 视觉规格' }, { label: 'MODEL LIMIT', value: '批量生图依赖外部服务' },
    ],
    sections: [
      { id: 'context', label: '01 / FACTS', title: '先澄清商品事实，再排卖点。', body: '产品名、价格、受众、平台、卖点和产品图构成输入。功效、合规和商家数据不得由模型自行补写。' },
      { id: 'flow', label: '02 / PAGE PLAN', title: '卖点顺序决定模块顺序。', body: '工作台生成页面结构、六图规划、文案和视觉执行规格，并可导出导演包；它把输入变成生产说明，不宣称已经上线或商业化。' },
      { id: 'evidence', label: '03 / INTERACTION', title: '页面输入与导出动作已真实执行。', body: '本地页面可完成输入、结构更新与导出动作；现有 3 张演示图来自 GPT Image 2，用于验证视觉规划，不代表每次输入都已自动批量完成生图。' },
      { id: 'limits', label: '04 / LIMITS', title: '最终图片和商业表述必须复核。', body: '批量生图依赖外部模型服务；产品摄影、功效声明、品牌一致性和上线结果仍需商家与人工验收。' },
    ], images: toolImage('ai-product-detail-page'),
  },
  {
    slug: 'rvc-local-voice-workflow', index: 'T5', eyebrow: 'SMALL SYSTEM / LOCAL AI DEPLOYMENT', title: 'LOCAL VOICE,\nVISIBLE STATE.',
    summary: '围绕本地部署、数据准备、特征提取、训练、索引、推理和状态回执组织声线工作流；公开页不携带任何模型或声音资产。',
    status: '运行时与源码门禁已复核', publicEvidence: '安全原因不附模型与音频画面',
    facts: [
      { label: 'SOURCE CHECK', value: '84 / 84 源码语法通过' }, { label: 'RUNTIME', value: '本地 GPU 运行时可用' },
      { label: 'UI FLOW', value: '浏览器上传与路径输入并存' }, { label: 'STATE', value: 'running / success / failed 回执' },
    ],
    sections: [
      { id: 'context', label: '01 / DEPLOYMENT', title: '本地部署的难点不只在安装。', body: '项目处理了 Python、旧版 Gradio、GPU 运行时和训练依赖之间的兼容问题，并保留浏览器交互与 API 两条入口。公开页只描述方法，不展示私人环境路径。' },
      { id: 'flow', label: '02 / PIPELINE', title: '数据 → 特征 → 训练 → Index → 推理 → 复核。', body: '训练数据进入预处理与特征提取后生成模型和索引；推理阶段选择输入音频与匹配索引，再对清晰度、音色和伪影进行人工复核。', points: ['浏览器上传与直接路径输入分别处理', '训练步骤互斥，失败时停止后续串联', '索引发现覆盖多个受控目录', '长期任务写入持久状态，避免只看 GPU 占用猜结果'] },
      { id: 'evidence', label: '03 / VERIFIED STATE', title: '运行时可用，状态可查询。', body: '本阶段只做只读复核：84 个项目源码文件通过语法解析，本地运行时可识别一个 GPU 设备，训练状态回执文件存在。历史交接记录还包含浏览器上传、训练互斥、索引刷新和状态查询的验证。' },
      { id: 'limits', label: '04 / PRIVACY', title: '声线资产不进入作品集。', body: '环境变量、模型名、PTH、Index、训练音频、声音样本、日志、人名、账号和私人路径全部排除。正式配音仍需同稿源音频、合法声音授权与人工听感验收。' },
    ], images: toolImage('rvc-local-voice-workflow'),
  },
];

function navigate(event: MouseEvent<HTMLAnchorElement>, path: string) {
  event.preventDefault();
  window.location.assign(path);
}

function DetailNav({ backHref = '/#projects', backLabel = '返回项目' }: { backHref?: string; backLabel?: string }) {
  return (
    <nav className="detail-nav" aria-label="详情页导航">
      <a href="/#home" onClick={(event) => navigate(event, '/#home')} className="brand-mark">MSN<span>®</span></a>
      <div><a href="/#projects" onClick={(event) => navigate(event, '/#projects')}>PROJECTS</a><a href="/gpt-image" onClick={(event) => navigate(event, '/gpt-image')}>GPT-IMAGE</a><a href="/#about" onClick={(event) => navigate(event, '/#about')}>ABOUT</a></div>
      <a className="detail-back" href={backHref} onClick={(event) => navigate(event, backHref)}><ArrowLeft /> {backLabel}</a>
    </nav>
  );
}

function LocalBaselineMarker() {
  if (!import.meta.env.DEV) return null;
  return <div className="working-marker"><strong>MSN PORTFOLIO</strong><span>WORKING BASELINE / LOCAL DEV</span></div>;
}

function DetailLightbox({ items, index, onClose, onMove }: { items: readonly string[]; index: number; onClose: () => void; onMove: (index: number) => void }) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft') onMove((index - 1 + items.length) % items.length);
      if (event.key === 'ArrowRight') onMove((index + 1) % items.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, items.length, onClose, onMove]);
  return <div className="detail-lightbox" role="dialog" aria-modal="true" aria-label="项目证据大图">
    <button onClick={onClose} aria-label="关闭"><X /></button>
    <button onClick={() => onMove((index - 1 + items.length) % items.length)} aria-label="上一张"><ChevronLeft /></button>
    <figure><img src={items[index]} alt="项目证据大图" /><figcaption>{String(index + 1).padStart(2, '0')} / {String(items.length).padStart(2, '0')}</figcaption></figure>
    <button onClick={() => onMove((index + 1) % items.length)} aria-label="下一张"><ChevronRight /></button>
  </div>;
}

export function ProjectDetailPage({ slug }: { slug: string }) {
  const project = details.find((item) => item.slug === slug) ?? details[0];
  const current = details.findIndex((item) => item.slug === project.slug);
  const previous = details[(current - 1 + details.length) % details.length];
  const next = details[(current + 1) % details.length];
  const [lightbox, setLightbox] = useState<number | null>(null);
  const originalScreens = projectViewerAssets[project.slug]?.originals;
  const evidenceImages = originalScreens?.length ? originalScreens.map(screen => screen.src) : project.images;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    document.title = `${project.title.replace('\n', ' ')} — MSN Portfolio`;
  }, [project]);

  return <main className="detail-page">
    <DetailNav />
    <LocalBaselineMarker />
    <header className="detail-hero">
      <div className="detail-hero-meta"><span>{project.eyebrow}</span><span>{project.status}</span></div>
      <span className="detail-index">{project.index}</span>
      <h1 className={/[\u3400-\u9fff]/.test(project.title) ? 'detail-title-cn' : undefined}>{project.title.split('\n').map((line) => <span key={line}>{line}</span>)}</h1>
      <p>{projectDemos[project.slug]?.intro ?? project.summary}</p>
      <a href={projectDemos[project.slug] ? '#project-demo' : '#context'}>查看项目演示 <ArrowRight /></a>
    </header>

    <section className="detail-facts" aria-label="关键事实">
      {project.facts.map((fact) => <div key={fact.label}><span>{fact.label}</span><strong>{fact.value}</strong></div>)}
    </section>

    {projectDemos[project.slug] && <section id="project-demo" className="detail-live-demo" aria-label="动态项目演示">
      <div className="detail-demo-heading"><span>PROJECT IN MOTION</span><h2>从真实操作，了解项目。</h2><p>{projectDemos[project.slug].platform}</p></div>
      <ProjectDemoView name={project.title.replace('\n', ' ')} scenes={projectDemos[project.slug].scenes} {...projectViewerAssets[project.slug]} />
      <ul className="app-project-features">{projectDemos[project.slug].features.map(feature => <li key={feature}>{feature}</li>)}</ul>
    </section>}

    {project.slug === 'msn-ai-radar' && <section aria-label="项目资料" style={{ margin: '40px auto', padding: '0 5vw', maxWidth: 1540 }}>
      <a href="/assets/core-projects/msn-ai-radar/MSN-ai-radar.pptx" download style={{ color: '#ffad7a' }}>下载项目介绍 PPT ↗</a>
    </section>}

    {project.slug === 'msn-task-workspace' && <section aria-label="项目演示与资料" style={{ margin: '40px auto', padding: '0 5vw', maxWidth: 1540 }}>
      <a href="/assets/core-projects/msn-task-workspace/MSN-task-workspace.pptx" download style={{ display: 'inline-block', marginTop: 12, color: '#ffad7a' }}>下载项目 PPT（含 GIF 演示） ↗</a>
    </section>}

    {project.slug === 'msn-content-publishing' && <section aria-label="项目资料" style={{ margin: '40px auto', padding: '0 5vw', maxWidth: 1540 }}>
      <a href="/assets/core-projects/msn-content-publishing.pptx" download style={{ display: 'inline-block', marginTop: 12, color: '#ffad7a' }}>下载项目介绍 PPT ↗</a>
      <figure style={{ margin: '24px 0 0' }}>
        <img src={project.images[0]} alt="MSN内容发布工作台：实际界面、代表成片与发布结果" style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 16 }} />
      </figure>
    </section>}

    {project.slug === 'digital-human-ai-review' && <section aria-label="项目资料" style={{ margin: '40px auto', padding: '0 5vw', maxWidth: 1540 }}>
      <a href="/assets/core-projects/digital-human.pptx" download style={{ color: '#ffad7a' }}>下载项目介绍 PPT ↗</a>
    </section>}

    {project.slug === 'v21-video-editing' && <section aria-label="项目资料" style={{ margin: '40px auto', padding: '0 5vw', maxWidth: 1540 }}>
      <a href="/assets/core-projects/v21.pptx" download style={{ color: '#ffad7a' }}>下载项目介绍 PPT ↗</a>
    </section>}

    {project.slug === 'local-ai-mobile' && <section aria-label="项目资料" style={{ margin: '40px auto', padding: '0 5vw', maxWidth: 1540 }}>
      <a href="/assets/core-projects/qwen.pptx" download style={{ color: '#ffad7a' }}>下载项目介绍 PPT ↗</a>
    </section>}

    {project.slug === 'finance-motion' && <section aria-label="项目资料" style={{ margin: '40px auto', padding: '0 5vw', maxWidth: 1540 }}>
      <a href="/assets/core-projects/finance.pptx" download style={{ color: '#ffad7a' }}>下载项目介绍 PPT ↗</a>
    </section>}

    <div className="detail-body">
      <aside aria-label="页内目录">
        <span>ON THIS PAGE</span>
        {project.sections.map((section) => <a key={section.id} href={`#${section.id}`}>{section.label}</a>)}
      </aside>
      <div className="detail-sections">
        {project.sections.map((section) => <section id={section.id} key={section.id}>
          <span>{section.label}</span><h2>{section.title}</h2><p>{section.body}</p>
          {section.points && <ul>{section.points.map((point) => <li key={point}>{point}</li>)}</ul>}
        </section>)}
      </div>
    </div>

    {!(projectViewerAssets[project.slug]?.video && evidenceImages.length === 0) && <section className="detail-evidence" aria-label="项目证据">
      <div><span>{project.video ? 'ORIGINAL FILM' : 'VISUAL EVIDENCE'}</span><h2>{project.video ? '观看已经完成的原创成片。' : originalScreens?.length ? '查看完整界面原图。' : evidenceImages.length ? '可核查的过程画面。' : '原创公开画面待制作。'}</h2></div>
      {project.video && <figure className="detail-video">
        <video controls playsInline preload="metadata" poster={project.video.poster} src={project.video.src}>
          当前浏览器不支持视频播放。
        </video>
        <figcaption><strong>{project.video.title}</strong><span>{project.video.meta}</span></figcaption>
      </figure>}
      {evidenceImages.length ? <div className="detail-evidence-grid">{evidenceImages.map((src, index) => <button key={src} onClick={() => setLightbox(index)}><img src={src} alt={originalScreens?.[index]?.title ?? `项目证据 ${index + 1}`} /><Maximize2 /></button>)}</div> : !project.video && <div className="detail-empty"><strong>ORIGINAL CASE / IN PROGRESS</strong><p>不会用第三方参考画面填充这个位置。</p></div>}
    </section>}

    <nav className="project-pagination" aria-label="项目切换">
      <a href={`/projects/${previous.slug}`} onClick={(event) => navigate(event, `/projects/${previous.slug}`)}><span><ArrowLeft /> PREVIOUS</span><strong>{previous.title.replace('\n', ' ')}</strong></a>
      <a href="/#projects" onClick={(event) => navigate(event, '/#projects')}><span>ALL PROJECTS</span><strong>返回核心项目列表</strong></a>
      <a href={`/projects/${next.slug}`} onClick={(event) => navigate(event, `/projects/${next.slug}`)}><span>NEXT <ArrowRight /></span><strong>{next.title.replace('\n', ' ')}</strong></a>
    </nav>
    {lightbox !== null && <DetailLightbox items={evidenceImages} index={lightbox} onClose={() => setLightbox(null)} onMove={setLightbox} />}
  </main>;
}

export function ToolDetailPage({ slug }: { slug: string }) {
  const tool = toolDetails.find((item) => item.slug === slug) ?? toolDetails[0];
  const current = toolDetails.findIndex((item) => item.slug === tool.slug);
  const previous = toolDetails[(current - 1 + toolDetails.length) % toolDetails.length];
  const next = toolDetails[(current + 1) % toolDetails.length];
  const [lightbox, setLightbox] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    document.title = `${tool.title.replace('\n', ' ')} — MSN Portfolio`;
  }, [tool]);

  return <main className="detail-page tool-detail-page">
    <DetailNav backHref="/#tools" backLabel="返回工具" />
    <LocalBaselineMarker />
    <header className="detail-hero tool-detail-hero">
      <div className="detail-hero-meta"><span>{tool.eyebrow}</span><span>{tool.status}</span></div>
      <span className="detail-index">{tool.index}</span>
      <h1>{tool.title.split('\n').map((line) => <span key={line}>{line}</span>)}</h1>
      <p>{tool.summary}</p>
      <a href="#context">查看真实证据 <ArrowRight /></a>
    </header>

    <section className="detail-facts" aria-label="工具关键事实">
      {tool.facts.map((fact) => <div key={fact.label}><span>{fact.label}</span><strong>{fact.value}</strong></div>)}
    </section>

    <div className="detail-body">
      <aside aria-label="页内目录"><span>ON THIS PAGE</span>{tool.sections.map((section) => <a key={section.id} href={`#${section.id}`}>{section.label}</a>)}</aside>
      <div className="detail-sections">{tool.sections.map((section) => <section id={section.id} key={section.id}>
        <span>{section.label}</span><h2>{section.title}</h2><p>{section.body}</p>
        {section.points && <ul>{section.points.map((point) => <li key={point}>{point}</li>)}</ul>}
      </section>)}</div>
    </div>

    <section className="detail-evidence" aria-label="工具公开证据">
      <div><span>PUBLIC EVIDENCE</span><h2>{tool.publicEvidence}</h2></div>
      {tool.images.length
        ? <div className="detail-evidence-grid">{tool.images.map((src, index) => <button key={src} onClick={() => setLightbox(index)}><img src={src} alt={`${tool.title.replace('\n', ' ')} 公开证据`} /><Maximize2 /></button>)}</div>
        : <div className="detail-empty"><strong>PRIVACY-GATED EVIDENCE</strong><p>只展示可核查结论；私人界面、模型、账号和素材未复制到网站。</p></div>}
    </section>

    <nav className="project-pagination" aria-label="工具切换">
      <a href={`/tools/${previous.slug}`} onClick={(event) => navigate(event, `/tools/${previous.slug}`)}><span><ArrowLeft /> PREVIOUS</span><strong>{previous.title.replace('\n', ' ')}</strong></a>
      <a href="/#tools" onClick={(event) => navigate(event, '/#tools')}><span>ALL TOOLS</span><strong>返回正式中小项目</strong></a>
      <a href={`/tools/${next.slug}`} onClick={(event) => navigate(event, `/tools/${next.slug}`)}><span>NEXT <ArrowRight /></span><strong>{next.title.replace('\n', ' ')}</strong></a>
    </nav>
    {lightbox !== null && <DetailLightbox items={tool.images} index={lightbox} onClose={() => setLightbox(null)} onMove={setLightbox} />}
  </main>;
}

export function GptImageDetailPage() {
  const [lightbox, setLightbox] = useState<number | null>(null);
  const seriesDetails = [
    {
      id: 'mistea', label: '01 / MISTEA', title: '柔和、雾感、面向消费品牌。', items: misteaImages,
      body: '围绕山雾气泡茶建立轻盈消费视觉：高键自然光、白色矿石或纸感台面、柔雾背景、冷凝水珠与果味色块。六张画面从视觉母版延展到口味广告、风味信息图、品牌触点、城市 Campaign 和广告分镜。',
      rules: ['主体：细长罐体与“山雾”字标保持稳定', '场景：白色矿石、茶叶、花果与城市触点', '光影：高键漫射光，阴影柔和且保留层次', '材质：冷凝罐身、天然石面和轻雾空气感', '镜头：静物编排、口味组合与生活场景连续展开'],
    },
    {
      id: 'robot-vacuum', label: '02 / ROBOT VACUUM', title: '克制、精密、面向智能家电。', items: robotVacuumImages,
      body: '围绕同一台扫地机器人与自集尘基站建立家电产品视觉：从客厅主视觉延展到厨房、地毯、夜间、归航与部件特写，保持机身结构和材质一致。',
      rules: ['主体：暖银色圆形机身、顶部雷达与深色前传感带', '基站：圆角立式机身与深色下部回收舱保持一致', '场景：客厅、厨房、地毯、夜间公寓与玄关', '光影：自然建筑光与夜景混合光服务产品结构', '镜头：低机位、归航正面与细节近景连续展开'],
    },
    {
      id: 'human-ccd', label: '03 / HUMAN CCD', title: '同一人物，不同生活切片。', items: humanCcdImages,
      body: '以同一位成年女性角色为身份母版，在卧室、咖啡馆、城市街头、海边、书店与雨夜中改变服装、发型、动作和表情，同时保持脸型、五官与右眼下小痣等身份锚点。',
      rules: ['身份：脸型、眼睛、鼻唇比例与右眼下小痣保持一致', '服装：针织、亚麻、夹克、风衣、书店层搭与雨衣', '动作：回眸、坐姿、行走、倚栏、挑书与撑伞', '表情：惊讶、自然笑、平静、专注与开怀大笑', '质感：CCD 直闪、真实皮肤纹理与轻微传感器颗粒'],
    },
  ];

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); document.title = 'GPT-image2 — MSN Portfolio'; }, []);

  return <main className="detail-page gpt-detail-page">
    <DetailNav /><LocalBaselineMarker />
    <header className="detail-hero gpt-detail-hero">
      <div className="detail-hero-meta"><span>GPT-IMAGE2 / SERIES</span><span>18 个横版系列画面</span></div>
      <span className="detail-index">G2</span>
      <h1><span>VISUAL SYSTEMS,</span><span>NOT SINGLE SHOTS.</span></h1>
      <p>用同一品牌母版延展广告、电商、信息图、触点和分镜，观察风格一致性与生产适配，而不是只展示孤立的漂亮图片。</p>
      <a href="#mistea">查看系列 <ArrowRight /></a>
    </header>
    {seriesDetails.map((series) => <section className="gpt-series" id={series.id} key={series.id}>
      <header><span>{series.label}</span><h2>{series.title}</h2><p>{series.body}</p><ul>{series.rules.map((rule) => <li key={rule}>{rule}</li>)}</ul></header>
      <div>{series.items.map((item) => { const index = gptImages.findIndex((entry) => entry.src === item.src); return <button key={item.src} onClick={() => setLightbox(index)}><img src={item.src} alt={`${item.series} 系列画面`} /><span>MSN</span><Maximize2 /></button>; })}</div>
    </section>)}
    <section className="gpt-method"><span>04 / METHOD</span><h2>主体 + 场景 + 光影 + 材质 + 镜头 + 约束。</h2><p>先用母版明确产品或人物的视觉锚点，再改变场景、服装、动作、光影与镜头，最后检查品牌结构、产品几何或人物身份是否仍属于同一系统。页面只描述从 18 张本地正式横版画面可核查的控制方法，不公开完整提示词、核心参数组合、客户或商业结果；没有真实初版与调整版证据时，也不补写迭代历史。</p></section>
    <nav className="project-pagination"><a href="/#gpt-image" onClick={(event) => navigate(event, '/#gpt-image')}><span><ArrowLeft /> HOME GALLERY</span><strong>返回横向作品带</strong></a><a href="/#projects" onClick={(event) => navigate(event, '/#projects')}><span>CORE PROJECTS</span><strong>继续浏览项目证据</strong></a><a href="/projects/ai-video-automation" onClick={(event) => navigate(event, '/projects/ai-video-automation')}><span>NEXT <ArrowUpRight /></span><strong>AI VIDEO AUTOMATION</strong></a></nav>
    {lightbox !== null && <DetailLightbox items={gptImages.map((item) => item.src)} index={lightbox} onClose={() => setLightbox(null)} onMove={setLightbox} />}
  </main>;
}
